import InsightsBody from './components/body/personalizedInsightsV2Body';
import InsightsHeader from './components/header/personalizedInsightsV2Header';

import { useParams } from 'react-router';
import { PersonalizedInsightsLoadingScreen } from './loadingScreen/loadingScreen';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { Config } from '@copilot/common/config';
import styles from './personalizedInsights.module.less';
import {
    IConnectionWithCommunicationStyleQuery,
    useConnectionWithCommunicationStyleQuery,
} from '@copilot/data/graphql/_generated';
import {
    Insights,
    isCommunicationStyle,
} from '@copilot/common/pages/personalizedInsightsV2/common/personalizedInsightsV2Types';

/**
 * Possible failures when generating a communication style analysis
 */
const INSIGHT_FAILURE_CODES = {
    ERROR: 'ERROR',
    INSUFFICIENT_DATA: 'INSUFFICIENT_DATA',
    PROFILE_MISSING: 'PROFILE_MISSING',
    CONFLICT: 'CONFLICT',
} as const;

type InsightFailureCode = (typeof INSIGHT_FAILURE_CODES)[keyof typeof INSIGHT_FAILURE_CODES];

const ERROR_MESSAGES: Record<InsightFailureCode, string> = {
    [INSIGHT_FAILURE_CODES.ERROR]:
        "It looks like we ran into an issue. Please refresh the page and we'll try again.",
    [INSIGHT_FAILURE_CODES.INSUFFICIENT_DATA]:
        "Uh oh... It seems like this LinkedIn profile doesn't have enough information to generate insights.",
    [INSIGHT_FAILURE_CODES.PROFILE_MISSING]:
        "Hmm... It looks like this profile doesn't exist anymore.",
    [INSIGHT_FAILURE_CODES.CONFLICT]:
        "It looks like we ran into an issue. Please refresh the page and we'll try again.",
} as const;

function toInsightsNew(
    record: IConnectionWithCommunicationStyleQuery | undefined
): Insights | undefined {
    if (isNil(record)) return undefined;
    const { linkedInProfile, communicationStyles } = record.campaignConnection;
    const primaryStyle = communicationStyles?.styles?.[0]?.style;
    const secondaryStyle = communicationStyles?.styles?.[1]?.style;
    if (!isCommunicationStyle(primaryStyle)) return undefined;
    return {
        firstName: linkedInProfile.firstName,
        lastName: linkedInProfile.lastName,
        title: linkedInProfile.title ?? undefined,
        commStyle: primaryStyle,
        secondaryCommStyle: isCommunicationStyle(secondaryStyle) ? secondaryStyle : undefined,
        alias: linkedInProfile.alias ?? undefined,
        liAvatarUrl: linkedInProfile.profilePictureUrl ?? undefined,
    };
}

function NewPersonalizedInsightsPage({ connectionId }: { connectionId: string }) {
    const { data, loading, error } = useConnectionWithCommunicationStyleQuery({
        variables: { connectionId },
    });

    const insights = !loading ? toInsightsNew(data) : undefined;

    let loadingFailureReason: string | undefined = undefined;
    if (loading) {
        loadingFailureReason = undefined;
    } else if (!isNil(error)) {
        loadingFailureReason = ERROR_MESSAGES[INSIGHT_FAILURE_CODES.ERROR];
    } else if (isEmpty(data?.campaignConnection?.communicationStyles?.styles)) {
        loadingFailureReason = ERROR_MESSAGES[INSIGHT_FAILURE_CODES.INSUFFICIENT_DATA];
    }

    return !loading && !isNil(insights) ? (
        <>
            <InsightsHeader
                insights={insights}
                communicationStyleHelpArticleUrl={Config.communicationStyleHelpArticleUrl}
            />
            <InsightsBody communicationStyle={insights.commStyle} />
        </>
    ) : (
        <PersonalizedInsightsLoadingScreen
            hasLoadingFailed={!isNil(loadingFailureReason)}
            loadingFailureReason={loadingFailureReason ?? ''}
        />
    );
}

function PersonalizedInsightsPageV2() {
    const { connectionId } = useParams<Record<'profileId' | 'connectionId', string>>();
    return (
        <div className={styles.insightsWrapper}>
            <NewPersonalizedInsightsPage connectionId={connectionId ?? ''} />
        </div>
    );
}

export { PersonalizedInsightsPageV2 };
